/* eslint-disable prefer-destructuring */
/* eslint-disable @typescript-eslint/prefer-enum-initializers */
import classNames from 'classnames';
import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { ITranslateProps, translate } from '../../mixins/translate';
import { useTranslate } from '../../mixins/translate/translate';
import { fetchGet } from '../../store/fetch';
import { Roles } from '../../utils/enums/Roles';
import { isCustomerVersion } from '../../utils/isCustomerVersion';
import { usePatchedDropdownMenu } from '../../utils/usePatchedDropdownMenu';
import { LeftTray, LeftTrayItem, LeftTrayList, PanelRouterLink } from '../elements';
import { NullableBoolean } from '../utils/NullableBoolean';
import RoleDependent from '../utils/RoleDependent';
import { CompanyMaintenanceCopy } from './CompanyMaintenanceCopy';
import ObjectList from './CompanyObjectsList';
import RelationsList from './CompanyRelationsList';
import UsageList from './CompanyUsageList';
import { UserList } from './CompanyUsersList';
import LicenceList from './LicenceList';

// eslint-disable-next-line no-shadow
export enum CompanyDetailsLists {
    User,
    Relation,
    Object,
    Usage,
    License,
}

interface ICompanyDetailsProps extends ITranslateProps {
    dispatch: (prop: any) => void;
    userRole: string;
    match: { params: any };
    location: {
        query: { list?: string };
    };
}

interface ICompanyDetailsState {
    message?: string;
    errorCode?: string;
    name?: string;
    storageMb?: string;
    status?: string;
    role?: string;
    state?: string;
    city?: string;
    zip?: string;
    street?: string;
    country?: string;
    isClient?: boolean;
    currentList: CompanyDetailsLists;
}

function mapStateToProps(state: any) {
    const { authentication } = state;
    return {
        userRole: authentication.role,
    };
}

class CompanyDetails extends React.Component<ICompanyDetailsProps, ICompanyDetailsState> {
    constructor(props: ICompanyDetailsProps) {
        super(props);

        let currentList = CompanyDetailsLists.User;

        if (props.location.query.list !== undefined) {
            currentList = parseInt(props.location.query.list, 10);
        }
        this.state = {
            currentList,
            isClient: false,
        };
    }

    public override async componentDidMount() {
        if (this.props.match.params.id) {
            await this.load();
        }
    }

    public override componentDidUpdate(prevProps: ICompanyDetailsProps) {
        if (this.props.location.query.list !== prevProps.location.query.list) {
            const currentList = parseInt(this.props.location.query.list ?? '0', 10);
            this.setState({ currentList });
        }
    }

    public override render() {
        const viewList = this.getCurrentListComponent();

        return (
            <div>
                <header id="topbar" className="ph10">
                    <div className="topbar-left">
                        <ol className="breadcrumb">
                            <li className="crumb-active">
                                <span className="fa fa fa-building pr10"></span>
                                <a href="#">{this.state.name}</a>
                            </li>
                            <li className="crumb-trail">
                                <Link to="/companies">{this.props.t('common.companies')}</Link>
                            </li>
                        </ol>
                    </div>
                    <div className="topbar-right hidden-xs hidden-sm">
                        {this.state.isClient === true && <CompanyMaintenanceCopy clientCompanyId={this.props.match.params.id} />}
                        <RoleDependent
                            allowed={[Roles.Administrator]}
                            content={
                                <Link
                                    to={{ pathname: '/companies/addrelation', search: `?companyId=${this.props.match.params.id}` }}
                                    className="btn btn-default btn-sm fw600 ml10"
                                >
                                    {' '}
                                    <span className="fa fa-plus pr5"></span> {this.props.t('company.addRelation')}
                                </Link>
                            }
                        />
                        {isCustomerVersion('ronik') && (
                            <RoleDependent
                                allowed={[Roles.Administrator]}
                                content={
                                    <Link
                                        to={{
                                            pathname: '/companies/addlicence',
                                            search: `?companyId=${this.props.match.params.id}&role=${this.state.role}`,
                                        }}
                                        className="btn btn-default btn-sm fw600 ml10"
                                    >
                                        {' '}
                                        <span className="fa fa-plus pr5"></span> {this.props.t('common.addItem', this.props.t('licence.licence'))}
                                    </Link>
                                }
                            />
                        )}

                        <RoleDependent
                            allowed={[Roles.Administrator, Roles.Client]}
                            content={
                                <AddUserButton companyId={this.props.match.params.id} companyRole={this.state.role} isClient={this.state.isClient} />
                            }
                        />

                        <RoleDependent
                            allowed={[Roles.Administrator, Roles.Client]}
                            content={
                                <PanelRouterLink
                                    to={`/companies/${this.props.match.params.id}/edit`}
                                    text={this.props.t('company.editCompany')}
                                    small
                                    icon="fa fa-edit"
                                />
                            }
                        />
                    </div>
                </header>

                <section id="content" className="table-layout animated fadeIn">
                    {/* Client details left pane */}
                    <LeftTray>
                        <address>
                            <br />
                            {this.state.name}
                            <br />
                            {this.state.street}
                            <br />
                            {this.state.zip}&nbsp;{this.state.city}
                        </address>
                        <LeftTrayList>
                            <LeftTrayItem title={this.props.t('company.companyRole')}>
                                {this.state.role && this.props.t(`common.${this.state.role}`)}
                            </LeftTrayItem>
                            {this.props.userRole === Roles.Administrator && (
                                <>
                                    <LeftTrayItem title={this.props.t('company.isClient')}>
                                        <NullableBoolean value={this.state.isClient} useSymbols={false} />
                                    </LeftTrayItem>
                                </>
                            )}
                            <LeftTrayItem title={this.props.t('dashboard.storageUsed')}>{this.state.storageMb} Gb</LeftTrayItem>
                            <LeftTrayItem title={this.props.t('common.status')}>
                                {(this.state.status === 'enabled' && <b className="text-success">{this.state.status}</b>) || (
                                    <b className="text-danger">{this.state.status}</b>
                                )}
                            </LeftTrayItem>
                        </LeftTrayList>
                    </LeftTray>
                    {/* Central client lists */}
                    <div className="tray tray-center fluid-width">
                        <div className="panel">
                            <div className="panel-heading">
                                <ul className="nav panel-tabs-border panel-tabs-merge panel-tabs">
                                    <li className={this.state.currentList === CompanyDetailsLists.User ? 'active' : ''}>
                                        <Link to={`/companies/${this.props.match.params.id}?list=${CompanyDetailsLists.User}`}>
                                            {this.props.t('common.users')}
                                        </Link>
                                    </li>
                                    <RoleDependent
                                        allowed={[Roles.Administrator]}
                                        content={
                                            <li className={this.state.currentList === CompanyDetailsLists.Relation ? 'active' : ''}>
                                                <Link to={`/companies/${this.props.match.params.id}?list=${CompanyDetailsLists.Relation}`}>
                                                    {this.props.t('company.relations')}
                                                </Link>
                                            </li>
                                        }
                                    />
                                    <RoleDependent
                                        allowed={[Roles.Administrator]}
                                        content={
                                            <li className={this.state.currentList === CompanyDetailsLists.Usage ? 'active' : ''}>
                                                <Link to={`/companies/${this.props.match.params.id}?list=${CompanyDetailsLists.Usage}`}>
                                                    {this.props.t('company.usage')}
                                                </Link>
                                            </li>
                                        }
                                    />
                                    <li className={this.state.currentList === CompanyDetailsLists.Object ? 'active' : ''}>
                                        <Link to={`/companies/${this.props.match.params.id}?list=${CompanyDetailsLists.Object}`}>
                                            {this.props.t('common.objects')}
                                        </Link>
                                    </li>
                                    {isCustomerVersion('ronik') && (
                                        <RoleDependent
                                            allowed={[Roles.Administrator]}
                                            content={
                                                <li className={this.state.currentList === CompanyDetailsLists.License ? 'active' : ''}>
                                                    <Link to={`/companies/${this.props.match.params.id}?list=${CompanyDetailsLists.License}`}>
                                                        {this.props.t('licence.licences')}
                                                    </Link>
                                                </li>
                                            }
                                        />
                                    )}
                                </ul>
                            </div>
                            <div className="panel-body ph20">
                                <div className="tab-content">
                                    <div className="tab-pane active">
                                        <div className="table-responsive">{viewList}</div>
                                    </div>
                                </div>{' '}
                                {/* tab-content*/}
                            </div>{' '}
                            {/* panel body */}
                        </div>{' '}
                        {/* panel panel */}
                    </div>{' '}
                    {/* tray-center */}
                </section>
            </div>
        );
    }

    private getCurrentListComponent() {
        switch (this.state.currentList) {
            case CompanyDetailsLists.License:
                return <LicenceList companyId={this.props.match.params.id} />;
            case CompanyDetailsLists.Object:
                return <ObjectList id={this.props.match.params.id} />;
            case CompanyDetailsLists.Relation:
                return <RelationsList id={this.props.match.params.id} />;
            case CompanyDetailsLists.Usage:
                return <UsageList id={this.props.match.params.id} />;
            case CompanyDetailsLists.User:
                return <UserList companyId={this.props.match.params.id} />;
            default:
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                throw new Error(`Unknown list '${this.state.currentList}'`);
        }
    }

    private async load() {
        let status = 200;
        const url = `/api/companies/${this.props.match.params.id}`;
        try {
            const response: any = await fetchGet(url);

            status = response.status;

            const json = response.json;
            if (status >= 400) {
                let code = 'DEFAULT';
                if (json && json.code) {
                    code = json.code;
                }
                throw new Error(code);
            }

            if (json.storageLimit) {
                json.storageMb = Math.ceil(json.storageLimit / 1024 / 1024);
            }

            this.setState(json);
        } catch (error) {
            console.error(error);
            this.setState({ errorCode: error.message });
        }
    }
}

// eslint-disable-next-line import/no-default-export
export default translate()(connect(mapStateToProps, undefined, undefined, { forwardRef: true })(CompanyDetails as any) as any);

// eslint-disable-next-line prettier/prettier
function AddUserButton({
    companyId,
    companyRole,
    isClient,
}: {
    companyId: string;
    companyRole?: string | undefined;
    isClient?: boolean | undefined;
}) {
    const t = useTranslate();
    const { buttonProps, itemProps, isOpen } = usePatchedDropdownMenu(2);
    const classes = classNames('navbar-btn btn-group ml10', { open: isOpen });

    // If company role is not administrator AND isClient is false, show dropdown with both options
    if (companyRole === 'owner' && isClient === false) {
        return (
            <div className={classes}>
                <button {...buttonProps} className="btn btn-sm dropdown-toggle fw600" data-toggle="dropdown" type="button">
                    <span className="fa fa-plus pr5"></span> {t('company.addUser')}
                </button>

                <ul role="menu" className="dropdown-menu pv5 animated animated-short fadeIn">
                    <li {...itemProps[0]}>
                        <Link
                            to={{
                                pathname: '/companies/adduser',
                                search: `?companyId=${companyId}`,
                            }}
                        >
                            {t('user.newUser')}
                        </Link>
                    </li>
                    <li {...itemProps[1]}>
                        <Link
                            to={{
                                pathname: '/companies/addExistingUser',
                                search: `?companyId=${companyId}`,
                            }}
                        >
                            {t('user.existingUser')}
                        </Link>
                    </li>
                </ul>
            </div>
        );
    }

    // Otherwise, show simple button (like addrelation)
    return (
        <Link to={{ pathname: '/companies/adduser', search: `?companyId=${companyId}` }} className="btn btn-default btn-sm fw600 ml10">
            <span className="fa fa-plus pr5"></span> {t('company.addUser')}
        </Link>
    );
}
